import type { Degree, Pc, Quality, ScaleId } from '@astrasonora/annotation-schema'
import { SCALE_IDS } from '@astrasonora/annotation-schema'

/** Semitones from the tonic, spec §1. */
const SCALES: Record<ScaleId, readonly number[]> = {
  ionian: [0, 2, 4, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
}

/** Modes as degrees of the parent major, index 0 = Ionian … 6 = Locrian (no scale array for Locrian). */
export const MODE_BY_PARENT_DEGREE: readonly (ScaleId | null)[] = [...SCALE_IDS, null]

export const mod12 = (n: number): Pc => (((n % 12) + 12) % 12) as Pc

export function scaleOf(id: ScaleId): readonly number[] {
  return SCALES[id]
}

export function inScale(rel: number, S: readonly number[]): boolean {
  return S.includes(mod12(rel))
}

export function degreeIndex(rel: number, S: readonly number[]): Degree | null {
  const i = S.indexOf(mod12(rel))
  return i < 0 ? null : (i as Degree)
}

/** Quality of the diatonic triad on degree `d` of scale `S` (spec §3). */
export function diatonicQuality(d: Degree, S: readonly number[]): Quality {
  const root = S[d]!
  const third = mod12(S[(d + 2) % 7]! - root)
  const fifth = mod12(S[(d + 4) % 7]! - root)
  if (fifth === 7 && third === 4) return 'maj'
  if (fifth === 7 && third === 3) return 'min'
  if (fifth === 6 && third === 3) return 'dim'
  return 'aug'
}

/** Pitch-class names as they are usually written for a key of that scale. */
const NAMES_SHARP = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B']
const NAMES_FLAT = ['C', 'D♭', 'D', 'E♭', 'E', 'F', 'G♭', 'G', 'A♭', 'A', 'B♭', 'B']
/** Tonics for which the key signature of the given scale is written with flats. */
const FLAT_TONICS: Record<ScaleId, readonly number[]> = {
  ionian: [1, 3, 5, 8, 10],
  lydian: [1, 3, 8, 10],
  mixolydian: [0, 1, 3, 5, 8, 10],
  dorian: [0, 1, 3, 5, 7, 8, 10],
  aeolian: [0, 2, 3, 5, 7, 10],
  phrygian: [0, 2, 3, 5, 7, 9, 10],
}

export function pitchName(pc: Pc, scale: ScaleId = 'ionian'): string {
  const flat = FLAT_TONICS[scale].includes(pc)
  return (flat ? NAMES_FLAT : NAMES_SHARP)[pc]!
}

export const SCALE_DISPLAY: Record<ScaleId, { mode: string; common: string | null }> = {
  ionian: { mode: 'Ionian', common: 'major' },
  aeolian: { mode: 'Aeolian', common: 'minor' },
  dorian: { mode: 'Dorian', common: null },
  phrygian: { mode: 'Phrygian', common: null },
  lydian: { mode: 'Lydian', common: null },
  mixolydian: { mode: 'Mixolydian', common: null },
}

/** Centre text of the map: `A minor`, `F Lydian`. */
export function keyName(tonic: Pc, scale: ScaleId): string {
  const d = SCALE_DISPLAY[scale]
  return `${pitchName(tonic, scale)} ${d.common ?? d.mode}`
}
